import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Button,
    FormGroup,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    Pagination,
    PaginationItem,
    PaginationLink,
    CardFooter,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import {
    getAllReportTypes,
    addReportType,
    updateReportType,
    deleteReportType,
    toggleReportTypeStatus
} from "../../api/ApiServices";

/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
    glassCard: {
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
    }
};

const ReportTypes = () => {
    const [reportTypes, setReportTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ name: "" });
    const [errors, setErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("All Status");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 10;

    const toggle = () => {
        setModal(!modal);
        if (modal) {
            setEditingItem(null);
            setFormData({ name: "" });
            setErrors({});
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const statusParam = filterStatus === "All Status" ? "" : (filterStatus === "Active" ? "1" : "0");
            const res = await getAllReportTypes(page, limit, searchTerm, statusParam);
            if (res.status === true) {
                setReportTypes(res.payload.reportTypes || []);
                setTotal(res.payload.total || 0);
                setTotalPages(res.payload.totalPages || 1);
            }
        } catch (error) {
            console.error("Error fetching report types:", error);
            Swal.fire("Error", "Failed to load report types", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchData();
        }, 500);
        return () => clearTimeout(handler);
    }, [page, searchTerm, filterStatus]);

    const validateForm = () => {
        let isValid = true;
        let newErrors = {};
        const name = formData.name.trim();

        if (!name) {
            newErrors.name = "Type Name is required";
            isValid = false;
        } else if (name.length < 2) {
            newErrors.name = "Type Name must be at least 2 characters long";
            isValid = false;
        } else if (name.length > 50) {
            newErrors.name = "Type Name cannot exceed 50 characters";
            isValid = false;
        } else if (/[^a-zA-Z0-9\s]/.test(name)) {
            newErrors.name = "Special characters are not allowed";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        try {
            const payload = { rpt_name: formData.name };
            if (editingItem) {
                const res = await updateReportType(editingItem.rpt_uuid, payload);
                if (res.status) {
                    setReportTypes(prev => prev.map(item =>
                        item.rpt_uuid === editingItem.rpt_uuid ? { ...item, rpt_name: formData.name } : item
                    ));
                    Swal.fire("Success", "Report type updated", "success");
                }
            } else {
                const res = await addReportType(payload);
                if (res.status) {
                    setReportTypes(prev => [res.payload, ...prev.slice(0, limit - 1)]);
                    setTotal(prev => prev + 1);
                    Swal.fire("Success", "Report type added", "success");
                }
            }
            toggle();
        } catch (error) {
            Swal.fire("Error", error.response?.data?.message || "Failed to save report type", "error");
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({ name: item.rpt_name });
        setModal(true);
    };

    const handleToggleStatus = async (id) => {
        try {
            const res = await toggleReportTypeStatus(id);
            if (res.status) {
                setReportTypes(prev => prev.map(item =>
                    item.rpt_uuid === id ? { ...item, rpt_status: item.rpt_status === "1" ? "0" : "1" } : item
                ));
            }
        } catch (error) {
            Swal.fire("Error", "Failed to update status", "error");
        }
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete Report Type?",
            text: "This will remove the report type from the active list.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            confirmButtonText: "Yes, delete"
        });

        if (confirm.isConfirmed) {
            try {
                const res = await deleteReportType(id);
                if (res.status) {
                    setReportTypes(prev => prev.filter(item => item.rpt_uuid !== id));
                    setTotal(prev => prev - 1);
                    if (reportTypes.length <= 1 && page > 1) {
                        setPage(page - 1);
                    }
                }
            } catch (error) {
                Swal.fire("Error", "Failed to delete report type", "error");
            }
        }
    };

    const renderPagination = () => {
        const totalPagesCount = Math.ceil(total / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        const delta = 2; // Number of pages to show on either side

        // Always show first page
        pages.push(
            <PaginationItem key={1} active={page === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setPage(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        // Show start ellipsis if needed
        if (page > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        // Show pages around current
        const rangeStart = Math.max(2, page - delta);
        const rangeEnd = Math.min(totalPagesCount - 1, page + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={page === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setPage(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        // Show end ellipsis if needed
        if (page < totalPagesCount - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        // Always show last page
        if (totalPagesCount > 1) {
            pages.push(
                <PaginationItem key={totalPagesCount} active={page === totalPagesCount}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setPage(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination className="pagination justify-content-end mb-0">
                <PaginationItem disabled={page === 1}>
                    <PaginationLink onClick={() => setPage(page - 1)} tabIndex="-1"><i className="fas fa-angle-left" /></PaginationLink>
                </PaginationItem>
                {pages}
                <PaginationItem disabled={page === totalPagesCount}>
                    <PaginationLink onClick={() => setPage(page + 1)}><i className="fas fa-angle-right" /></PaginationLink>
                </PaginationItem>
            </Pagination>
        );
    };

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow border-0" style={styles.glassCard}>
                            <CardHeader className="border-0 bg-transparent py-4">
                                <Row className="align-items-center">
                                    <Col md="4">
                                        <h3 className="mb-0 text-dark font-weight-700">
                                            <i className="ni ni-bullet-list-67 text-danger mr-2" /> Report Types Management
                                        </h3>
                                    </Col>
                                    <Col md="8">
                                        <div className="d-flex justify-content-md-end flex-wrap" style={{ gap: "10px" }}>
                                            <Input
                                                placeholder="Search report types..."
                                                type="text"
                                                className="form-control-alternative"
                                                style={{ width: "250px", borderRadius: "8px" }}
                                                value={searchTerm}
                                                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                                            />
                                            <Input
                                                type="select"
                                                className="form-control-alternative"
                                                style={{ width: "150px", borderRadius: "8px" }}
                                                value={filterStatus}
                                                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                                            >
                                                <option>All Status</option>
                                                <option>Active</option>
                                                <option>Inactive</option>
                                            </Input>
                                            <Button color="primary" onClick={() => { setEditingItem(null); setFormData({ name: "" }); setModal(true); }} style={{ borderRadius: "10px" }}>
                                                <i className="fas fa-plus-circle mr-2" /> Add Report Type
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody className="p-0">
                                <div className="table-responsive">
                                    <Table className="align-items-center table-flush" hover>
                                        <thead className="thead-light">
                                            <tr>
                                                <th scope="col" style={{ padding: "16px 24px" }}>Type Name</th>
                                                <th scope="col">Status</th>
                                                <th scope="col">Added On</th>
                                                <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading && reportTypes.length === 0 ? (
                                                <tr><td colSpan="4" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td></tr>
                                            ) : reportTypes.length === 0 ? (
                                                <tr><td colSpan="4" className="text-center py-5 text-muted">No report types found.</td></tr>
                                            ) : (
                                                reportTypes.map((rt) => (
                                                    <tr key={rt.rpt_uuid}>
                                                        <td style={{ padding: "16px 24px" }}><span className="font-weight-600 text-dark">{rt.rpt_name}</span></td>
                                                        <td>
                                                            <Badge
                                                                color={rt.rpt_status === "1" ? "success" : "danger"}
                                                                pill
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => handleToggleStatus(rt.rpt_uuid)}
                                                            >
                                                                {rt.rpt_status === "1" ? "Active" : "Inactive"}
                                                            </Badge>
                                                        </td>
                                                        <td>{new Date(rt.createdAt).toLocaleDateString()}</td>
                                                        <td className="text-right" style={{ paddingRight: "24px" }}>
                                                            <UncontrolledDropdown>
                                                                <DropdownToggle className="btn-icon-only" role="button" size="sm" color="">
                                                                    <i className="fas fa-ellipsis-v" style={{ color: "#6c757d" }} />
                                                                </DropdownToggle>
                                                                <DropdownMenu className="dropdown-menu-arrow" right container="body">
                                                                    <DropdownItem onClick={() => handleEdit(rt)}>Rename</DropdownItem>
                                                                    <DropdownItem onClick={() => handleToggleStatus(rt.rpt_uuid)}>
                                                                        {rt.rpt_status === "1" ? "Deactivate" : "Activate"}
                                                                    </DropdownItem>
                                                                    <DropdownItem divider />
                                                                    <DropdownItem onClick={() => handleDelete(rt.rpt_uuid)} className="text-danger">Delete</DropdownItem>
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                            <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                                <span className="text-muted small">
                                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
                                </span>
                                {renderPagination()}
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={modal} toggle={toggle} centered>
                <ModalHeader toggle={toggle} className="border-0">
                    {editingItem ? "Rename Report Type" : "Add New Report Type"}
                </ModalHeader>
                <ModalBody>
                    <form onSubmit={handleSubmit} noValidate>
                        <FormGroup>
                            <label className="form-control-label">Type Name</label>
                            <Input
                                type="text"
                                placeholder="e.g. Spam"
                                required
                                value={formData.name}
                                onChange={(e) => {
                                    setFormData({ ...formData, name: e.target.value });
                                    if (errors.name) setErrors({ ...errors, name: null });
                                }}
                                style={{ borderRadius: "8px", minHeight: "45px" }}
                                autoFocus
                                invalid={!!errors.name}
                            />
                            {errors.name && <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>{errors.name}</div>}
                        </FormGroup>
                        <Button color="primary" block type="submit" style={{ minHeight: "45px", borderRadius: "10px" }}>
                            {editingItem ? "Update" : "Save"} Report Type
                        </Button>
                    </form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default ReportTypes;
